import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Printer } from "lucide-react";
import BrandMark from "../components/BrandMark";

/**
 * Standalone printable kitchen extract — opens in a new tab.
 * User triggers browser print (⌘P / Ctrl-P) → "Save as PDF" to export.
 * NO PRICING ANYWHERE (safe for the kitchen floor).
 *
 * Print-friendly: A4-ish page, large fonts, checkboxes per line so the
 * kitchen can tick items as they finish.
 */
export default function PrintableKitchenExtract() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/orders/${id}/kitchen.json`)
      .then((r) => {
        setData(r.data);
        // Auto-set tab title for "Save as PDF" filename
        document.title = `${r.data.order_no} — Kitchen`;
        // Auto-open print dialog after data renders
        setTimeout(() => window.print(), 600);
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [id]);

  if (err) {
    return <div className="p-10 text-center text-destructive">{err}</div>;
  }
  if (!data) {
    return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <>
      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          .print-page { padding: 0; max-width: none; }
        }
        @page { margin: 0.5in; }
      `}</style>

      <div className="min-h-screen bg-white text-black">
        {/* Floating Print button — only visible on screen */}
        <div className="no-print fixed top-4 right-4 z-10">
          <button
            onClick={() => window.print()}
            className="h-10 px-4 rounded-xl bg-black text-white text-sm font-medium inline-flex items-center gap-2 shadow-lg"
          >
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>

        <div className="print-page max-w-3xl mx-auto p-10">
          {/* Header */}
          <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-black">
            <div>
              <BrandMark size="md" />
              <div className="text-xs text-gray-600 mt-3">Kitchen Production Sheet</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-gray-600 mb-1">Order</div>
              <div className="text-3xl font-light tracking-tight">{data.order_no}</div>
            </div>
          </div>

          {/* Meta line */}
          <div className="grid grid-cols-3 gap-4 mb-8 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Customer</div>
              <div className="font-medium">{data.customer || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Pickup Date</div>
              <div className="font-medium">{data.pickup_date || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Location</div>
              <div className="font-medium">{data.location_name || "—"}</div>
            </div>
          </div>

          {/* Groups */}
          {data.groups.map((g) => (
            <section key={g.category} className="mb-7" style={{ pageBreakInside: "avoid" }}>
              <h2 className="text-base font-semibold uppercase tracking-wider border-b border-black pb-1 mb-3">
                {g.label}
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="text-left font-normal pb-1 w-6"></th>
                    <th className="text-left font-normal pb-1">Item</th>
                    <th className="text-center font-normal pb-1 w-20">Ordered</th>
                    <th className="text-center font-normal pb-1 w-20">Fulfilled</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((it) => (
                    <tr key={it.name} className="border-t border-gray-200">
                      <td className="py-2">
                        <span className="inline-block h-4 w-4 border border-black" />
                      </td>
                      <td className="py-2 font-medium">{it.name}</td>
                      <td className="py-2 text-center tabular-nums">{it.quantity}</td>
                      <td className="py-2 text-center tabular-nums">
                        {it.fulfilled_quantity !== it.quantity ? (
                          <strong>{it.fulfilled_quantity}</strong>
                        ) : (
                          <span className="text-gray-300">— —</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}

          {/* Packaging — separate sheet feel */}
          {data.packaging_items?.length > 0 && (
            <section className="mb-7" style={{ pageBreakInside: "avoid" }}>
              <h2 className="text-base font-semibold uppercase tracking-wider border-b border-black pb-1 mb-3">
                Packaging Re-order
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="text-left font-normal pb-1 w-6"></th>
                    <th className="text-left font-normal pb-1">Item</th>
                    <th className="text-center font-normal pb-1 w-20">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {data.packaging_items.map((p) => (
                    <tr key={p.name} className="border-t border-gray-200">
                      <td className="py-2"><span className="inline-block h-4 w-4 border border-black" /></td>
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2 text-center tabular-nums">{p.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {data.notes && (
            <div className="mt-8 pt-4 border-t border-gray-300">
              <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Notes</div>
              <p className="text-sm italic">{data.notes}</p>
            </div>
          )}

          <div className="mt-12 pt-4 border-t border-gray-300 grid grid-cols-2 gap-8 text-xs text-gray-600">
            <div>
              <div className="h-12 border-b border-gray-400 mb-1"></div>
              Kitchen signature
            </div>
            <div>
              <div className="h-12 border-b border-gray-400 mb-1"></div>
              Date completed
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
